/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Input, OnInit} from '@angular/core';
import * as _ from 'lodash';

import template from './weather-targeting.component.html';
import {ADN_TARGETING_TYPES} from '../targeting-constants';
import {TargetingTemplateHook} from './targeting-template.component';

@Component({
  standalone: false,
  selector: 'adn-weather-targeting',
  template
})
export class WeatherTargetingComponent implements OnInit {
  @Input() model: any[];
  @Input() tabController: any;
  @Input() overridableTargeting: any;
  @Input() targetType: any = ADN_TARGETING_TYPES.weather;
  @Input() underModel: any;
  @Input() mode: string;
  @Input() isLockedDown: boolean;
  @Input() mandatory: boolean;

  input: any = {
    enabled: false,
    value: 20,
    type: 'ABOVE_CELSIUS'
  };

  readonly weatherTypes = [
    {value: 'ABOVE_CELSIUS', label: 'targeting.weather.ABOVE'},
    {value: 'BELOW_CELSIUS', label: 'targeting.weather.BELOW'}
  ];

  readonly templateHook: TargetingTemplateHook = {
    getTemplateData: () => {
      this.prepareForSave();
      const model = this.model || [];
      const entry = model[0];
      return {
        vm: _.cloneDeep(this.input),
        model: _.cloneDeep(model),
        constant: ADN_TARGETING_TYPES.weather,
        summary: entry && _.isFinite(entry.value) && entry.type
          ? `${entry.value}°C ${entry.type}`
          : ''
      };
    },
    copyTemplate: (tpl: any) => this.copyTemplate(tpl)
  };

  ngOnInit(): void {
    this.initFromModel();

    if (!this.tabController) {
      return;
    }

    const prepareForSave = () => this.prepareForSave();
    const copyTemplate = (tpl: any) => this.copyTemplate(tpl);
    this.tabController.register(ADN_TARGETING_TYPES.weather.widget, () => this.initFromModel(), copyTemplate);
    this.tabController.callBeforeSubmit(prepareForSave);
    this.tabController.registerAllTabs(prepareForSave);
  }

  syncModel(): void {
    this.prepareForSave();
  }

  private initFromModel(): void {
    const existing = _.cloneDeep(_.get(this.model, [0])) || {};
    const value = this.toFiniteNumber(existing.value);
    this.input = {
      enabled: value !== null && !!existing.type,
      value: value !== null ? value : 20,
      type: existing.type || 'ABOVE_CELSIUS'
    };
  }

  private prepareForSave(): void {
    if (!_.isArray(this.model)) {
      return;
    }
    this.model.length = 0;
    const value = this.toFiniteNumber(_.get(this.input, ['value']));
    const type = _.get(this.input, ['type']);
    if (this.input.enabled && value !== null && type) {
      this.model.push({value, type});
    }
  }

  private toFiniteNumber(value: any): number | null {
    if (_.isNil(value) || value === '') {
      return null;
    }
    const numeric = _.isNumber(value) ? value : _.toNumber(value);
    return _.isFinite(numeric) ? numeric : null;
  }

  private copyTemplate(tpl: any): void {
    if (!_.isArray(this.model)) {
      return;
    }
    const copied = _.cloneDeep(_.get(tpl, ['data', 'model'])) || [];
    this.model.length = 0;
    Array.prototype.push.apply(this.model, copied);
    this.initFromModel();
  }
}
